import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams, NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { KeywordBankTable } from "./KeywordBankTable";
import { AddThemeModal } from "./AddThemeModal";
import { ThemePanel } from "./ThemePanel";
import { AddKeywordToThemeModal } from "./AddKeywordToThemeModal";
import { ThemeGroupedView } from "./ThemeGroupedView";
import { useGetKeywordThemes, useAddKeywordToTheme, useBulkAddKeywordsToTheme, useRemoveKeywordFromTheme, useAutoThemeSuggestions } from "@/resources/keyword-theme";
import type { KeywordTheme } from "@/types/keyword-theme";
import { DEFAULT_TABLE_PAGE_SIZE } from "@/components/shared/TablePagination";
import { AddKeywordsModal } from "@/components/shared/AddKeywordsModal";
import { Button } from "@/components/ui/button";
import { TabHeader } from "@/components/shared/TabHeader";
import { isKeywordBankImporting } from "@/utils/keywordBankImporting";
import {
  Plus,
  Star,
  Layers,
  BarChart3,
  X,
  RefreshCw,
  Database,
  Download,
  ArrowRight,
  Tag,
} from "lucide-react";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { AISparkles } from "@/components/ui/ai-sparkles";
import { ProductSectionEmptyState } from "@/components/product-detail/ProductSectionEmptyState";
import { CombinedFilterDropdown } from "@/components/shared/CombinedFilterDropdown";
import { StickyToolbarProvider } from "@/hooks/useStickyToolbar";
import { ClearableInput } from "@/components/shared/ClearableInput";
import { BulkActionsBar } from "@/components/shared/BulkActionsBar";
import { toast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Keyword, UtilizationTag } from "@/types/keyword";
import { productApi } from "@/resources/product";
import { keywordApi } from "@/resources/keyword";
import { KeywordMasterListAction } from "@/constants";
import { getBulkMasterListState } from "./utils";
import { DEFAULT_KEYWORD_FILTERS, FILTER_COLUMNS, DEFAULT_RANGE_FILTERS, RANGE_FILTER_COLUMNS, TEXT_FILTER_COLUMNS } from "./constants";
import type { RangeFilterState } from "./constants";
import { landscapeApi } from "@/resources/landscape";
import { rankTrackerApi } from "@/resources/rank-tracker";
import { useAppData } from "@/contexts/AppDataContext";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import type { HeroKeywordsSortField } from "@/types";
import { filterKeywordsForBank, parseTermList, sortHeroKeywords } from "./keywordBankClient";
import { handleApiError } from "@/utils";
import { MIN_KEYWORD_LENGTH } from "@/utils/keywordValidation";
import { ExportKeywordsModal } from "./ExportKeywordsModal";
import { keywordBankRunApi } from "@/resources/keyword-bank-run";
import { KeywordBankBuildState, shouldShowKeywordBankEmptyState } from "./KeywordBankBuildState";

export enum Segment {
  ALL_KEYWORDS = "all-keywords",
  MASTER_LIST = "master-list",
}

const SEGMENTS = [
  { id: Segment.ALL_KEYWORDS, label: "All Keywords" },
  { id: Segment.MASTER_LIST, label: "Master List" },
];

interface KeywordBankContentV2Props {
  productId: number;
  heroAsin: string;
  // AUT-256: ProductKeywordsPage is this component's only renderer, and it keeps the
  // segment in the URL (`?segment=`) so a segment can be cmd/right-clicked into a new
  // browser tab. The segment strip below is a NavLink strip whose own navigation is the
  // only thing that changes it -- there is no local fallback state and no change
  // callback; the caller re-derives `segment` from its own useSearchParams on the
  // resulting render.
  segment: Segment;
}

export function KeywordBankContentV2({ productId, heroAsin, segment: activeSegment }: KeywordBankContentV2Props) {
  // Only used to build the segment links' hrefs -- reads the same location the
  // controlling page's own useSearchParams reads, so the "other" params on the URL
  // survive a segment click untouched (see the preserved-param test in
  // ProductKeywordsPage.test.tsx).
  const [urlSearchParams] = useSearchParams();
  const segmentHref = (id: Segment): string => {
    const next = new URLSearchParams(urlSearchParams);
    next.set("segment", id);
    return `?${next.toString()}`;
  };
  const [addThemeSelectedIds, setAddThemeSelectedIds] = useState<number[]>([]);
  const [selectedKeywords, setSelectedKeywords] = useState<Set<number>>(
    new Set(),
  );
  const [addKeywordsModalOpen, setAddKeywordsModalOpen] = useState(false);
  const [quickKeyword, setQuickKeyword] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [kwFilters, setKwFilters] = useState<Record<string, string[]>>(DEFAULT_KEYWORD_FILTERS);
  const [rangeFilters, setRangeFilters] = useState<RangeFilterState>(DEFAULT_RANGE_FILTERS);
  // AUT-419: free-text include/exclude filters. Keys match TEXT_FILTER_COLUMNS below.
  const [textFilters, setTextFilters] = useState<Record<string, string>>({ phrases: "", exclude: "" });
  const [matchModes, setMatchModes] = useState<Record<string, string>>({ phrasesMode: "any" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [sortField, setSortField] = useState<HeroKeywordsSortField>("kpg");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [addThemeOpen, setAddThemeOpen] = useState(false);
  const [thematicBannerDismissed, setThematicBannerDismissed] = useState(false);
  const [addThemeInitialKeywordId, setAddThemeInitialKeywordId] = useState<number | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<KeywordTheme | null>(null);
  const [addKeywordToThemeOpen, setAddKeywordToThemeOpen] = useState(false);
  const [addKeywordTheme, setAddKeywordTheme] = useState<KeywordTheme | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const keywordByIdRef = useRef<Map<number, Keyword>>(new Map());

  const { productId: productIdParam } = useParams<{ productId?: string }>();
  const navigate = useNavigate();
  const { selectedBrandId, isLoadingBrands } = useAppData();
  const readOnly = useViewerReadOnly();

  const onOpenLandscape = useCallback((landscapeId: number) => {
    if (productIdParam) {
      navigate(`/products/${productIdParam}/landscapes/${landscapeId}`);
    }
  }, [navigate, productIdParam]);

  useEffect(() => {
    keywordByIdRef.current = new Map();
    setSelectedKeywords(new Set());
  }, [productId]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => window.clearTimeout(t);
  }, [search]);

  const {
    data: keywordResponse,
    isPending: isKeywordsPending,
    isFetching,
    isError: isKeywordsError,
    error: keywordsError,
  } = productApi.useGetHeroProductKeywords(String(productId), selectedBrandId);

  const allKeywords = keywordResponse ?? [];

  const { data: keywordBankRunResponse } = keywordBankRunApi.useGetKeywordBankRun(productId);
  const keywordBankRun = keywordBankRunResponse?.run;

  const { data: themes = [], isLoading: isLoadingThemes } = useGetKeywordThemes(productId);
  const { mutateAsync: addKeywordToTheme } = useAddKeywordToTheme();
  const { mutateAsync: bulkAddKeywordsToTheme } = useBulkAddKeywordsToTheme();
  const { mutateAsync: removeKeywordFromTheme } = useRemoveKeywordFromTheme();

  // Spec 5a: while under 3 themes, Signal AI surfaces suggestions
  // automatically rather than waiting for an explicit "Ask Signal AI" click.
  // Fetched via the normal query cache (5 min staleTime, invalidated by the
  // theme-count-changing mutations in keyword-theme.api.ts) -- not a
  // scheduled background job, just moving the trigger from click to mount.
  const autoSuggestEnabled = !readOnly && themes.length < 3;
  const { data: autoThemeSuggestions, isLoading: isAutoSuggesting } = useAutoThemeSuggestions(
    productId,
    autoSuggestEnabled,
  );

  // Keep selectedTheme in sync with the latest themes data so the panel
  // reflects renames / keyword additions without needing a page refresh.
  useEffect(() => {
    if (!selectedTheme) return;
    const updated = themes.find((t) => t.id === selectedTheme.id);
    if (updated && updated !== selectedTheme) setSelectedTheme(updated);
  }, [themes, selectedTheme]);


  const keywordThemeMap = useMemo(() => {
    const map = new Map<number, { themeId: number; themeName: string }>();
    for (const theme of themes) {
      for (const tk of theme.keywords) {
        map.set(tk.keyword_id, { themeId: theme.id, themeName: theme.name });
      }
    }
    return map;
  }, [themes]);

  const keywordMap = useMemo(() => {
    const map = new Map<number, Keyword>();
    for (const k of allKeywords) map.set(k.id, k);
    return map;
  }, [allKeywords]);

  const dataRanges = useMemo(() => {
    const ranges: Record<string, { min: number; max: number }> = {};
    for (const col of RANGE_FILTER_COLUMNS) {
      let min = Infinity;
      let max = -Infinity;
      for (const kw of allKeywords) {
        const val = kw[col.field as keyof typeof kw];
        const num = val != null ? Number(val) : NaN;
        if (Number.isFinite(num)) {
          if (num < min) min = num;
          if (num > max) max = num;
        }
      }
      if (min !== Infinity && max !== -Infinity) {
        ranges[col.id] = { min, max };
      }
    }
    return ranges;
  }, [allKeywords]);

  // Maps keyword_id → String(theme.id) so the filter is rename-safe
  const keywordThemeNameMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const theme of themes) {
      for (const tk of theme.keywords) {
        map.set(tk.keyword_id, String(theme.id));
      }
    }
    return map;
  }, [themes]);

  const bankFilterOpts = useMemo(
    () => ({
      masterListOnly: activeSegment === Segment.MASTER_LIST,
      search: debouncedSearch,
      utilization: kwFilters.utilization,
      source: kwFilters.source,
      theme: kwFilters.theme ?? [],
      rangeFilters,
      phrasesContaining: parseTermList(textFilters.phrases),
      phrasesMode: matchModes.phrasesMode === "all" ? ("all" as const) : ("any" as const),
      excludeKeywords: parseTermList(textFilters.exclude),
    }),
    [
      activeSegment,
      debouncedSearch,
      kwFilters.utilization,
      kwFilters.source,
      kwFilters.theme,
      rangeFilters,
      textFilters.phrases,
      textFilters.exclude,
      matchModes.phrasesMode,
    ],
  );

  const themeFilterColumn = useMemo(() => ({
    id: "theme",
    label: "Theme",
    options: [
      ...themes.map((t) => ({ value: String(t.id), label: t.name })),
      { value: "__unthemed__", label: "Unthemed" },
    ],
  }), [themes]);

  const allFilterColumns = useMemo(
    () => (themes.length > 0 ? [...FILTER_COLUMNS, themeFilterColumn] : FILTER_COLUMNS),
    [themes, themeFilterColumn],
  );

  const filteredSortedKeywords = useMemo(() => {
    const filtered = filterKeywordsForBank(allKeywords, bankFilterOpts, keywordThemeNameMap);
    return sortHeroKeywords(filtered, sortField, sortDirection);
  }, [allKeywords, bankFilterOpts, keywordThemeNameMap, sortField, sortDirection]);

  const totalFiltered = filteredSortedKeywords.length;

  const rows = useMemo(() => {
    const start = page * pageSize;
    return filteredSortedKeywords.slice(start, start + pageSize);
  }, [filteredSortedKeywords, page, pageSize]);

  const awaitingBrand = isLoadingBrands || selectedBrandId == null;
  const isInitialKeywordLoad =
    awaitingBrand || (isKeywordsPending && !keywordResponse);

  useEffect(() => {
    for (const k of allKeywords) {
      keywordByIdRef.current.set(k.id, k);
    }
  }, [allKeywords]);

  const { mutateAsync: bulkUpdateMasterList } = productApi.useBulkUpdateMasterList();
  const { mutate: createLandscape } = landscapeApi.useCreateLandscape();
  const { mutateAsync: addHeroProductKeywords, isPending: isAddingKeywords } =
    keywordApi.useAddHeroProductKeywords();
  const { mutate: deleteKeyword } = keywordApi.useDeleteKeyword();
  const { mutateAsync: addRankTrackerKeywords } = rankTrackerApi.useAddRankTrackerKeywords();
  const onBulkAddToRankTracker = async () => {
    if (!selectedBrandId) return;
    const selectedKeywordArray = selectedArray?.map((id) => keywordByIdRef.current.get(id))
      .filter((k): k is Keyword => k !== undefined);

    const notTracked = selectedKeywordArray.filter(
      (k) => !k.utilizationTags?.includes(UtilizationTag.TRACKED)
    );

    setSelectedKeywords(new Set());

    if (notTracked.length === 0) {
      toast({
        title: "Already tracked",
        description: `All selected keywords are already being tracked.`,
      });
      return;
    }

    const attempt = async (): Promise<void> => {
      toast({
        title: "Adding to Rank Tracker",
        description: `Adding ${notTracked.length} keyword${notTracked.length !== 1 ? "s" : ""}...`,
      });

      try {
        const result = await addRankTrackerKeywords({
          brandId: selectedBrandId,
          heroAsin,
          keywords: notTracked?.map((k) => k.search),
          source: "manual",
          // Target the selected rows, not whichever duplicate matches the phrase.
          keywordIds: notTracked?.map((k) => k.id),
        });
        toast({
          title: "Added to Rank Tracker",
          description: `${result.inserted} keyword${result.inserted !== 1 ? "s" : ""} added${result.skipped > 0 ? `, ${result.skipped} already tracked` : ""}.`,
        });
      } catch (error) {
        handleApiError(error, () => { attempt(); });
      }
    };
    await attempt();
  };


  const onBulkSetTheme = async (themeId: number) => {
    const keywordIds = Array.from(selectedKeywords);
    setSelectedKeywords(new Set());
    const themeName = themes.find((t) => t.id === themeId)?.name ?? "theme";
    try {
      const result = await bulkAddKeywordsToTheme({ productId, themeId, keywordIds });
      const addedCount = result?.added_count ?? keywordIds.length;
      toast({
        title: "Theme set",
        description: `${addedCount} keyword${addedCount !== 1 ? "s" : ""} added to "${themeName}".`,
      });
    } catch (error) {
      handleApiError(error);
    }
  };

  const onCreateLandscape = () => {
    const selectedKeywordArray = selectedArray
      .map((id) => keywordByIdRef.current.get(id))
      .filter((k): k is Keyword => k !== undefined);

    const searchPhrases = selectedKeywordArray?.map((k) => k.search);

    setSelectedKeywords(new Set());

    searchPhrases.forEach((searchPhrase) => {
      createLandscape({
        search_phrase: searchPhrase,
        hero_asin: heroAsin,
        brand_id: selectedBrandId,
      });
    });

    toast({
      title: "Landscapes Added",
      description: `Generating landscapes started for ${searchPhrases.join(", ")} for hero product ${heroAsin}.`,
    });
  };

  const onMasterList = useCallback(
    async (keywordIds: number[], action: KeywordMasterListAction) => {
      setSelectedKeywords(new Set());
      const isAdding = action === KeywordMasterListAction.ADD;
      toast({
        title: isAdding ? "Adding to Master List..." : "Removing from Master List...",
        description: `${isAdding ? "Adding" : "Removing"} ${keywordIds.length} keyword${keywordIds.length !== 1 ? "s" : ""} ${isAdding ? "to" : "from"} Master List.`,
      });
      try {
        const result = await bulkUpdateMasterList({ productId, keywordIds, action });
        const actuallyUpdated = result?.updated_count ?? keywordIds.length;
        if (actuallyUpdated > 0) {
          toast({
            title: isAdding ? "Added to Master List" : "Removed from Master List",
            description: `${actuallyUpdated} keyword${actuallyUpdated !== 1 ? "s" : ""} ${isAdding ? "added" : "removed"} successfully.`,
          });
        }
      } catch (error) {
        handleApiError(error, () => onMasterList(keywordIds, action));
      }
    },
    [bulkUpdateMasterList, productId],
  );

  const onDeleteKeyword = useCallback(
    (keywordId: number) => {
      setSelectedKeywords((prev) => {
        const next = new Set(prev);
        next.delete(keywordId);
        return next;
      });
      deleteKeyword({ productId, keywordId });
    },
    [deleteKeyword, productId],
  );

  const onBulkUpdateMasterList = useCallback(async () => {
    const selectedKeywordArray = selectedArray
      .map((id) => keywordByIdRef.current.get(id))
      .filter((k): k is Keyword => k !== undefined);

    if (
      !hasSelection ||
      selectedKeywordArray.length !== selectedKeywords.size
    ) {
      return;
    }

    const state = getBulkMasterListState(selectedKeywords, selectedKeywordArray);
    if (!state) return;

    const allInMasterList = selectedKeywordArray.every((kw) => kw.is_master_list);
    const keywordIds = selectedKeywordArray?.map((kw) => kw.id);
    const action = allInMasterList
      ? KeywordMasterListAction.REMOVE
      : KeywordMasterListAction.ADD;

    setSelectedKeywords(new Set());

    const isAdding = action === KeywordMasterListAction.ADD;
    const attempt = async (): Promise<void> => {
      toast({
        title: isAdding ? "Adding to Master List..." : "Removing from Master List...",
        description: `${isAdding ? "Adding" : "Removing"} ${keywordIds.length} keyword${keywordIds.length !== 1 ? "s" : ""} ${isAdding ? "to" : "from"} Master List.`,
      });

      try {
        await bulkUpdateMasterList({ productId, keywordIds, action });
        toast({
          title: isAdding ? "Added to Master List" : "Removed from Master List",
          description: `${keywordIds.length} keyword${keywordIds.length !== 1 ? "s" : ""} ${isAdding ? "added" : "removed"} successfully.`,
        });
      } catch (error) {
        handleApiError(error, () => { attempt(); });
      }
    };
    await attempt();
  }, [selectedKeywords, productId, bulkUpdateMasterList]);

  const handleUpdateUtilizationTags = (
    _keywordId: number,
    _tags: UtilizationTag[],
  ) => {};

  const handleSelectKeyword = (keywordId: number, selected: boolean) => {
    setSelectedKeywords((prev) => {
      const next = new Set(prev);
      if (selected) next.add(keywordId);
      else next.delete(keywordId);
      return next;
    });
  };

  const handleSortChange = (field: HeroKeywordsSortField) => {
    if (isFetching) return;
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
    setPage(0);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(0);
  };


  const handleChangeKeywordTheme = useCallback(
    // addKeyword already handles "keyword currently in another theme" atomically
    // server-side (deletes the old assignment, reassigns head term if needed,
    // dispatches analysis for both themes) — calling removeKeywordFromTheme
    // first was redundant and, on partial failure, could leave the keyword
    // themeless instead of in its original theme.
    async (keywordId: number, _fromThemeId: number, toThemeId: number) => {
      try {
        await addKeywordToTheme({ productId, themeId: toThemeId, keywordId });
        const themeName = themes.find((t) => t.id === toThemeId)?.name ?? "theme";
        toast({ title: "Keyword moved", description: `Moved to "${themeName}".` });
      } catch {
        // useAddKeywordToTheme's onError (handleApiError) already shows a
        // toast on failure -- just swallow here so the promise doesn't
        // surface as an unhandled rejection.
      }
    },
    [addKeywordToTheme, productId, themes],
  );

  // Resets selection/search/filters whenever the active segment changes -- (AUT-256)
  // the `segment` prop is driven entirely by the URL now, so this fires whenever
  // `?segment=` changes, including on a direct load at `?segment=master-list`, since
  // this effect also runs on mount (a no-op there: state is already at these
  // defaults). It depends only on `activeSegment`, so typing in the search box or
  // touching a filter does not re-fire it. Pagination itself is reset by the effect
  // below, which already depends on activeSegment.
  useEffect(() => {
    setSelectedKeywords(new Set());
    setSearch("");
    setDebouncedSearch("");
    setKwFilters(DEFAULT_KEYWORD_FILTERS);
    setRangeFilters(DEFAULT_RANGE_FILTERS);
    setTextFilters({ phrases: "", exclude: "" });
    setMatchModes({ phrasesMode: "any" });
  }, [activeSegment]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, kwFilters, rangeFilters, activeSegment]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalFiltered / pageSize) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [totalFiltered, pageSize, page]);

  const selectedArray = Array.from(selectedKeywords);
  const hasSelection = selectedArray.length > 0;

  const bulkMasterListState = useMemo(() => {
    const items = Array.from(selectedKeywords)
      .map((id) => keywordByIdRef.current.get(id))
      .filter((k): k is Keyword => k != null);
    return getBulkMasterListState(selectedKeywords, items);
  }, [selectedKeywords, allKeywords]);

  const allCount = allKeywords.length;
  const masterCount = useMemo(
    () => allKeywords.filter((k) => k.is_master_list).length,
    [allKeywords],
  );

  const submitHeroKeywords = async (added: string[]) => {
    if (selectedBrandId == null) return;
    const result = await addHeroProductKeywords({
      productId,
      brandId: selectedBrandId,
      keywords: added,
    });
    const parts: string[] = [];
    if (result.inserted > 0) {
      parts.push(`${result.inserted} saved`);
    }
    if (result.skipped_duplicates > 0) {
      parts.push(`${result.skipped_duplicates} already in bank`);
    }
    toast({
      title: "Keywords",
      description:
        parts.length > 0
          ? parts.join(" · ")
          : "No new keywords were added.",
    });
  };

  const handleAddKeywords = async (added: string[], starredKeywords: string[]) => {
    if (selectedBrandId == null) return;
    const result = await addHeroProductKeywords({
      productId,
      brandId: selectedBrandId,
      keywords: added,
    });
    const parts: string[] = [];
    if (result.inserted > 0) {
      parts.push(`${result.inserted} saved`);
    }
    if (result.skipped_duplicates > 0) {
      parts.push(`${result.skipped_duplicates} already in bank`);
    }
    toast({
      title: "Keywords",
      description:
        parts.length > 0
          ? parts.join(" · ")
          : "No new keywords were added.",
    });
    if (starredKeywords.length > 0 && result.keywords.length > 0) {
      const starredSet = new Set(starredKeywords.map((k) => k.toLowerCase()));
      const starredIds = result.keywords
        .filter((k) => starredSet.has(k.search.toLowerCase()))
        .map((k) => k.id);
      if (starredIds.length > 0) {
        await bulkUpdateMasterList({
          productId,
          keywordIds: starredIds,
          action: KeywordMasterListAction.ADD,
        });
      }
    }
    setAddKeywordsModalOpen(false);
  };

  const handleQuickAddKeyword = async () => {
    const trimmed = quickKeyword.trim();
    if (trimmed === "" || selectedBrandId == null || isAddingKeywords) return;
    if (trimmed.length < MIN_KEYWORD_LENGTH) {
      toast({
        title: "Keyword too short",
        description: "Keywords must be at least 2 characters.",
        variant: "destructive",
      });
      return;
    }
    try {
      await submitHeroKeywords([trimmed]);
      setQuickKeyword("");
    } catch {
      // handleApiError from mutation
    }
  };

  // AUT-437: the shared tab header, on every state of the tab. `disabled` is the loading / error
  // variant (actions present but inert, the kebab item disabled too); `withMenu` is false on the
  // empty state, where there is nothing to export.
  const renderTabHeader = ({ disabled = false, withMenu = true }: { disabled?: boolean; withMenu?: boolean } = {}) => (
    <TabHeader
      title="Keywords"
      menuItems={
        withMenu ? (
          <DropdownMenuItem disabled={disabled} onClick={() => setExportModalOpen(true)}>
            <Download className="h-4 w-4 mr-2" />
            Export keywords…
          </DropdownMenuItem>
        ) : undefined
      }
    >
      {!readOnly && (
        <Button variant="outline" disabled={disabled} onClick={() => setAddThemeOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Theme
        </Button>
      )}
      {!readOnly ? (
        <Button disabled={disabled} onClick={() => setAddKeywordsModalOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Keywords
        </Button>
      ) : null}
    </TabHeader>
  );

  if (awaitingBrand) {
    return (
      <div className="min-w-0 space-y-6">
        {renderTabHeader({ disabled: true })}
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-20 min-h-[200px]">
          <RefreshCw className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading brand…</p>
        </div>
      </div>
    );
  }

  if (isKeywordsError) {
    return (
      <div className="min-w-0 space-y-6">
        {renderTabHeader({ disabled: true })}
        <div className="flex flex-1 flex-col items-center justify-center gap-2 py-16 px-4 text-center">
          <p className="text-sm text-destructive font-medium">Could not load keywords</p>
          <p className="text-xs text-muted-foreground max-w-md">
            {(keywordsError as Error)?.message ?? "Request failed. Check the network tab or try again."}
          </p>
        </div>
      </div>
    );
  }

  if (shouldShowKeywordBankEmptyState(keywordBankRun, allKeywords.length === 0 && !isInitialKeywordLoad)) {
    return (
      <div className="min-w-0 space-y-6">
        {renderTabHeader({ withMenu: false })}
        <KeywordBankBuildState run={keywordBankRun} />
        <AddThemeModal
          open={addThemeOpen}
          onOpenChange={(v) => { setAddThemeOpen(v); if (!v) { setAddThemeInitialKeywordId(null); setAddThemeSelectedIds([]); } }}
          productId={productId}
          allKeywords={allKeywords}
          existingThemesCount={themes.length}
          initialKeywordId={addThemeInitialKeywordId}
          initialKeywordIds={addThemeSelectedIds}
        />
        <AddKeywordsModal
          open={addKeywordsModalOpen}
          onOpenChange={setAddKeywordsModalOpen}
          onAddKeywords={handleAddKeywords}
          isSubmitting={isAddingKeywords}
          maxKeywords={1000}
          showMasterListOption
        />
        <ProductSectionEmptyState
          icon={Database}
          title="No keywords tracked yet."
          description="Add keywords or a theme to start scoring this product's search coverage."
          actions={[
            {
              label: "Add Keywords",
              icon: Plus,
              onClick: () => setAddKeywordsModalOpen(true),
            },
          ]}
        />
      </div>
    );
  }

  // The banner's NUMBER is still the spec's slot count ("how many more to
  // reach 3", not a suggestion count) -- but now that useAutoThemeSuggestions
  // has actually asked Signal AI, visibility is gated on there being a real
  // suggestion behind it, not just an available slot. Fixes the case this
  // whole auto-fetch was built for: a banner promising opportunities that
  // turn out empty once the modal opens.
  const remainingThematicOpportunities = Math.max(0, 3 - themes.length);
  const hasRealThematicSuggestions = (autoThemeSuggestions?.suggestions?.length ?? 0) > 0;
  const showThematicBanner =
    autoSuggestEnabled && !thematicBannerDismissed && remainingThematicOpportunities > 0 && hasRealThematicSuggestions;

  return (
    <StickyToolbarProvider>
    <div className="min-w-0 space-y-4">
      {renderTabHeader({ disabled: isInitialKeywordLoad || isKeywordBankImporting(keywordBankRun) })}

      <KeywordBankBuildState run={keywordBankRun} />

      {showThematicBanner && (
        <Alert className="gradient-border-ai flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <AISparkles className="h-4 w-4 shrink-0" />
            <AlertTitle className="mb-0">
              You have up to {remainingThematicOpportunities} new thematic grouping{" "}
              {remainingThematicOpportunities === 1 ? "opportunity" : "opportunities"}.
            </AlertTitle>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="text-primary"
              onClick={() => setAddThemeOpen(true)}
            >
              Review suggestions
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setThematicBannerDismissed(true)}
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </Alert>
      )}

      <div className="flex items-center">
        {/* AUT-256: real links so a segment can be cmd/right-clicked into a new browser
            tab. Classes mirror the `segmented` variant in components/ui/tabs.tsx, so this
            strip is visually identical to the Radix TabsList/TabsTrigger it replaces --
            including on hover: the active/inactive hover classes are mutually exclusive
            (not layered, as the removed Radix `data-[state=active]:hover:bg-primary`
            override was working around) so hovering the active segment can't be pulled
            back to the inactive hover:bg-muted. No onClick here -- the NavLink's own
            navigation is the only thing that changes the segment; adding a redundant
            setSearchParams call in the caller double-pushed history (AUT-256 fix round). */}
        <div className="h-auto justify-center rounded-md bg-muted/50 p-1 inline-flex items-center text-muted-foreground">
          {SEGMENTS.map((segment) => (
            <NavLink
              key={segment.id}
              to={segmentHref(segment.id)}
              className={cn(
                "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                "rounded-[6px] px-3 py-1.5",
                activeSegment === segment.id
                  ? "bg-primary text-primary-foreground shadow-sm hover:bg-primary"
                  : "hover:text-foreground hover:bg-muted",
              )}
            >
              {segment.label}
              <span className="ml-1.5 text-xs opacity-70">
                ({segment.id === Segment.ALL_KEYWORDS
                  ? allCount
                  : masterCount})
              </span>
            </NavLink>
          ))}
        </div>
      </div>

      {!readOnly ? (
        <BulkActionsBar
          selectedCount={selectedKeywords.size}
          onClearSelection={() => setSelectedKeywords(new Set())}
          sticky
        >
          {bulkMasterListState ? (
            <DropdownMenuItem onClick={onBulkUpdateMasterList}>
              <Star className="h-4 w-4 mr-2" />
              {bulkMasterListState.label}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onClick={onCreateLandscape}>
            <Layers className="h-4 w-4 mr-2" />
            Add Landscape
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onBulkAddToRankTracker}>
            <BarChart3 className="h-4 w-4 mr-2" />
            Add to Rank Tracker
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Tag className="h-4 w-4 mr-2" />
              Set theme…
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="bg-popover z-50 max-h-[280px] overflow-y-auto">
              {themes.length === 0 ? (
                <DropdownMenuItem disabled>No themes yet</DropdownMenuItem>
              ) : (
                themes.map((t) => (
                  <DropdownMenuItem key={t.id} onClick={() => onBulkSetTheme(t.id)}>
                    {t.name}
                  </DropdownMenuItem>
                ))
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  // AUT-201: carry the current Bulk Actions selection into the
                  // modal; snapshotted here so clearing the selection while the
                  // modal is open can't empty the new theme.
                  setAddThemeSelectedIds(Array.from(selectedKeywords));
                  setAddThemeOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create new theme…
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </BulkActionsBar>
      ) : null}

      <AddKeywordsModal
        open={addKeywordsModalOpen}
        onOpenChange={setAddKeywordsModalOpen}
        onAddKeywords={handleAddKeywords}
        isSubmitting={isAddingKeywords}
        maxKeywords={1000}
        showMasterListOption
      />

      <div className="flex items-center gap-3 flex-wrap">
        <ClearableInput
          value={search}
          onChange={setSearch}
          placeholder="Search keywords..."
          className="h-9"
          wrapperClassName="w-64"
        />

        <CombinedFilterDropdown
          columns={allFilterColumns}
          activeFilters={kwFilters}
          onFiltersChange={setKwFilters}
          rangeColumns={RANGE_FILTER_COLUMNS}
          activeRangeFilters={rangeFilters}
          onRangeFiltersChange={setRangeFilters}
          dataRanges={dataRanges}
          textColumns={TEXT_FILTER_COLUMNS}
          activeTextFilters={textFilters}
          onTextFiltersChange={setTextFilters}
          matchModes={matchModes}
          onMatchModesChange={setMatchModes}
        />

        <span className="text-sm text-muted-foreground ml-auto tabular-nums">
          Showing {totalFiltered.toLocaleString()} of{" "}
          {(activeSegment === Segment.ALL_KEYWORDS ? allCount : masterCount).toLocaleString()}{" "}
          {(activeSegment === Segment.ALL_KEYWORDS ? allCount : masterCount) === 1 ? "keyword" : "keywords"}
        </span>
      </div>

      <div className="min-w-0">
        {activeSegment === Segment.MASTER_LIST ? (
          <ThemeGroupedView
            productId={productId}
            heroAsin={heroAsin}
            themes={themes}
            masterKeywords={filteredSortedKeywords}
            keywordMap={keywordMap}
            isLoading={isLoadingThemes || isInitialKeywordLoad}
            selectedKeywords={selectedKeywords}
            onSelectKeyword={handleSelectKeyword}
            onOpenTheme={setSelectedTheme}
            onAddKeyword={(theme) => { setAddKeywordTheme(theme); setAddKeywordToThemeOpen(true); }}
            onUpdateMasterList={onMasterList}
            onDeleteKeyword={onDeleteKeyword}
            onOpenLandscape={onOpenLandscape}
            onOpenAddTheme={(kwId) => { setAddThemeInitialKeywordId(kwId ?? null); setAddThemeOpen(true); }}
            readOnly={readOnly}
          />
        ) : (
          <KeywordBankTable
            keywords={rows}
            selectedKeywords={selectedKeywords}
            onSelectKeyword={handleSelectKeyword}
            onUpdateMasterList={onMasterList}
            onDeleteKeyword={onDeleteKeyword}
            onOpenLandscape={onOpenLandscape}
            heroAsin={heroAsin}
            productId={productId}
            isLoading={isInitialKeywordLoad || isFetching}
            disableSortInteractions={isFetching}
            totalItems={totalFiltered}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            readOnly={readOnly}
            keywordThemeMap={keywordThemeMap}
            themes={themes}
            onAddKeywordToTheme={(keywordId, themeId) =>
              addKeywordToTheme({ productId, themeId, keywordId })
            }
            onRemoveKeywordFromTheme={(keywordId, themeId) =>
              removeKeywordFromTheme({ productId, themeId, keywordId })
            }
            onChangeKeywordTheme={handleChangeKeywordTheme}
            onOpenAddTheme={(kwId) => { setAddThemeInitialKeywordId(kwId ?? null); setAddThemeOpen(true); }}
          />
        )}
      </div>

      <ExportKeywordsModal
        open={exportModalOpen}
        onOpenChange={setExportModalOpen}
        allKeywords={allKeywords}
        themes={themes}
      />

      <AddThemeModal
        open={addThemeOpen}
        onOpenChange={(v) => { setAddThemeOpen(v); if (!v) { setAddThemeInitialKeywordId(null); setAddThemeSelectedIds([]); } }}
        productId={productId}
        allKeywords={allKeywords}
        existingThemesCount={themes.length}
        initialKeywordId={addThemeInitialKeywordId}
        initialKeywordIds={addThemeSelectedIds}
        prefetchedSuggestions={autoSuggestEnabled ? autoThemeSuggestions?.suggestions : undefined}
        isPrefetchingSuggestions={autoSuggestEnabled && isAutoSuggesting}
      />

      <ThemePanel
        theme={selectedTheme}
        productId={productId}
        onClose={() => setSelectedTheme(null)}
        onAddKeyword={() => { setAddKeywordTheme(selectedTheme); setAddKeywordToThemeOpen(true); }}
        onThemeUpdated={setSelectedTheme}
      />

      <AddKeywordToThemeModal
        open={addKeywordToThemeOpen}
        onOpenChange={(open) => { setAddKeywordToThemeOpen(open); if (!open) setAddKeywordTheme(null); }}
        theme={addKeywordTheme}
        productId={productId}
        brandId={selectedBrandId ?? null}
        allKeywords={allKeywords}
      />

    </div>
    </StickyToolbarProvider>
  );
}
